/**
 * 이벤트 관리 - 이벤트 생성 및 활성 이벤트 목록
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getActiveEvents, createEvent, endEvent } from '../services/api';
import StoreBadge from '../components/StoreBadge';
import { isBridgeApp } from '../utils/isBridgeApp';
import './EventManagePage.css';

const EventManagePage = () => {
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 생성 폼
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    eventName: '',
    description: '',
    coinAmount: '',
    startDate: new Date().toISOString().slice(0, 10),
    endDate: new Date().toISOString().slice(0, 10),
    maxParticipants: '',
  });
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState(null);

  // 종료 확인
  const [endingId, setEndingId] = useState(null);

  const storeId = localStorage.getItem('store_id');

  const loadEvents = async () => {
    try {
      const res = await getActiveEvents(storeId);
      setEvents(res.events || []);
    } catch (err) {
      setError(err.message || '이벤트 목록 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleCreate = async () => {
    if (!formData.eventName.trim()) {
      setFormError('이벤트명을 입력해주세요');
      return;
    }
    const coinAmt = parseInt(formData.coinAmount, 10);
    if (isNaN(coinAmt) || coinAmt === 0) {
      setFormError('주화 수량을 입력해주세요 (0 제외)');
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      setFormError('기간을 입력해주세요');
      return;
    }
    if (!storeId) {
      setFormError('매장 정보가 없습니다. 다시 로그인해주세요');
      return;
    }

    setCreating(true);
    setFormError(null);

    try {
      await createEvent({
        eventName: formData.eventName.trim(),
        description: formData.description.trim() || undefined,
        coinAmount: coinAmt,
        startDate: formData.startDate,
        endDate: formData.endDate,
        storeId: storeId,
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants, 10) : undefined,
      });
      setShowForm(false);
      setFormData({
        eventName: '', description: '', coinAmount: '',
        startDate: new Date().toISOString().slice(0, 10),
        endDate: new Date().toISOString().slice(0, 10),
        maxParticipants: '',
      });
      setLoading(true);
      await loadEvents();
    } catch (err) {
      setFormError(err.message || '이벤트 생성 실패');
    } finally {
      setCreating(false);
    }
  };

  const handleEnd = async (eventId) => {
    try {
      await endEvent(eventId);
      setEndingId(null);
      setLoading(true);
      await loadEvents();
    } catch (err) {
      alert(err.message || '이벤트 종료 실패');
    }
  };

  return (
    <div className="event-manage-page">
      <header className="evm-header">
        <button className="btn-back" onClick={() => navigate('/')}>
          ← 메뉴
        </button>
        {/* 헤더를 눌러 전체화면 — **태블릿 앱에서만** (2026-08-06 요구).
            웹(크롬)에서는 제목을 눌렀다고 전체화면이 되면 안 된다. */}
        <div className="evm-header-center" onClick={() => {
          if (!isBridgeApp()) return;
          const el = document.documentElement;
          (el.requestFullscreen || el.webkitRequestFullscreen)?.call(el)?.catch?.(() => {});
        }}>
          <h1>🎪 이벤트</h1>
          <StoreBadge />
        </div>
        <div className="evm-header-spacer" />
      </header>

      <main className="evm-content">
        {/* 생성 버튼 */}
        {!showForm && (
          <button className="btn-create-event" onClick={() => setShowForm(true)}>
            + 새 이벤트 만들기
          </button>
        )}

        {/* 생성 폼 */}
        {showForm && (
          <div className="evm-form-card">
            <h3>새 이벤트</h3>

            <div className="evm-field">
              <label>이벤트명 *</label>
              <input
                type="text"
                placeholder="예: 동물퀴즈 이벤트"
                value={formData.eventName}
                onChange={(e) => setFormData({ ...formData, eventName: e.target.value })}
                maxLength={100}
              />
            </div>

            <div className="evm-field">
              <label>설명</label>
              <input
                type="text"
                placeholder="이벤트 설명 (선택)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="evm-field">
              <label>보상 주화 * (음수=회수)</label>
              <input
                type="number"
                placeholder="예: 5"
                value={formData.coinAmount}
                onChange={(e) => setFormData({ ...formData, coinAmount: e.target.value })}
              />
            </div>

            <div className="evm-field-row">
              <div className="evm-field">
                <label>시작일 *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div className="evm-field">
                <label>종료일 *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </div>
            </div>

            <div className="evm-field">
              <label>최대 참여자 (비우면 무제한)</label>
              <input
                type="number"
                placeholder="무제한"
                value={formData.maxParticipants}
                onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })}
                min="1"
              />
            </div>

            {formError && <div className="evm-form-error">{formError}</div>}

            <div className="evm-form-buttons">
              <button className="btn-form-cancel" onClick={() => { setShowForm(false); setFormError(null); }} disabled={creating}>
                취소
              </button>
              <button className="btn-form-submit" onClick={handleCreate} disabled={creating}>
                {creating ? '생성 중...' : '이벤트 생성'}
              </button>
            </div>
          </div>
        )}

        {/* 이벤트 목록 */}
        {loading && <div className="evm-loading"><div className="evm-spinner" /><p>로딩 중...</p></div>}

        {!loading && events.length === 0 && !showForm && (
          <div className="evm-empty">
            <span>🎪</span>
            <p>진행 중인 이벤트가 없습니다</p>
          </div>
        )}

        {!loading && events.length > 0 && (
          <div className="evm-event-list">
            {events.map((ev) => (
              <div key={ev.event_id} className="evm-event-card">
                <div className="evm-event-top">
                  <div className="evm-event-name">
                    {ev.coin_amount > 0 ? '🪙' : '🔻'} {ev.event_name}
                    {!ev.store_id && <span className="evm-common-badge">공통</span>}
                  </div>
                  <div className={`evm-event-coin ${ev.coin_amount > 0 ? 'earn' : 'deduct'}`}>
                    {ev.coin_amount > 0 ? `+${ev.coin_amount}` : ev.coin_amount}개
                  </div>
                </div>
                {ev.description && <p className="evm-event-desc">{ev.description}</p>}
                <div className="evm-event-meta">
                  <span>{ev.start_date} ~ {ev.end_date}</span>
                  <span>참여 {ev.participant_count}{ev.max_participants ? `/${ev.max_participants}` : ''}명</span>
                </div>

                {/* 종료 확인 (공통 이벤트는 admin에서만 종료 가능) */}
                {!ev.store_id ? (
                  <div className="evm-common-note">관리자에서 종료 가능</div>
                ) : endingId === ev.event_id ? (
                  <div className="evm-end-confirm">
                    <span>정말 종료할까요?</span>
                    <button className="btn-end-yes" onClick={() => handleEnd(ev.event_id)}>종료</button>
                    <button className="btn-end-no" onClick={() => setEndingId(null)}>취소</button>
                  </div>
                ) : (
                  <button className="btn-end-event" onClick={() => setEndingId(ev.event_id)}>
                    이벤트 종료
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {error && <div className="evm-error">{error}</div>}
      </main>
    </div>
  );
};

export default EventManagePage;
